import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useOwnerNotifications,
  useUnreadNotificationsCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@/hooks/useOwnerNotifications";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { RESTOCK_DELIVERED_TYPE } from "@/lib/restockDeliveredNotify";
import { DeliveredNotifyToaster } from "@/components/owner/DeliveredNotifyToaster";

function notificationHref(n: {
  property_id: string;
  maintenance_issue_report_id?: string | null;
  maintenance_issue_id?: string | null;
  cleaning_task_id?: string | null;
  type: string;
}): string | null {
  if (n.maintenance_issue_report_id) {
    return `/properties/${n.property_id}?tab=maintenance&focusIssueId=${n.maintenance_issue_report_id}`;
  }
  if (n.maintenance_issue_id) {
    return `/properties/${n.property_id}?tab=maintenance&focusIssueId=${n.maintenance_issue_id}`;
  }
  if (n.type === RESTOCK_DELIVERED_TYPE) {
    return `/properties/${n.property_id}?tab=maintenance`;
  }
  if (n.cleaning_task_id) {
    return `/properties/${n.property_id}?tab=reports`;
  }
  return null;
}

export function NotificationBell({ variant = "default" }: { variant?: "default" | "shell" } = {}) {
  const navigate = useNavigate();
  const { data: notifications = [] } = useOwnerNotifications(10);
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const handleNotificationClick = (notification: (typeof notifications)[0]) => {
    if (!notification.is_read) {
      markRead.mutate(notification.id);
    }
    const href = notificationHref(notification);
    if (href) navigate(href);
  };

  return (
    <>
      <DeliveredNotifyToaster />
      <Popover>
        <PopoverTrigger asChild>
          {variant === "shell" ? (
            <button
              type="button"
              aria-label="Notifications"
              className="relative flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-white text-[#0f1720] transition-shadow hover:shadow-[inset_0_0_0_1.5px_#cfdbe2] max-lg:h-11 max-lg:w-11"
            >
              <Bell className="h-5 w-5" strokeWidth={1.8} />
              {unreadCount > 0 && (
                <span className="absolute -right-[3px] -top-[3px] flex h-5 min-w-5 items-center justify-center rounded-full bg-[#24a89a] px-[5px] text-[11.5px] font-bold text-white shadow-[0_0_0_2px_#f4f7f9]">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          ) : (
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#24a89a] text-xs font-bold text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[400px] max-w-[calc(100vw-1.5rem)] p-0" align="end">
          <div className="flex items-center justify-between border-b border-[#e6edf1] px-4 py-3">
            <h4 className="text-[15px] font-bold text-[#0f1720]">Notifications</h4>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-2 text-xs font-bold text-[#15776d]"
                onClick={() => markAllRead.mutate()}
              >
                Mark all read
              </Button>
            )}
          </div>
          <ScrollArea className="h-[320px]">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm font-medium text-[#64748b]">No notifications</div>
            ) : (
              <div>
                {notifications.map((notification) => {
                  const unread = !notification.is_read;
                  const delivered = notification.type === RESTOCK_DELIVERED_TYPE;
                  return (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        "flex w-full gap-3 border-b border-[#f4f7f9] px-5 py-4 text-left transition-colors last:border-b-0",
                        unread ? "bg-[#f7fbfa]" : "bg-white hover:bg-[#f8fafc]",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-[7px] h-2 w-2 shrink-0 rounded-full",
                          unread ? "bg-[#24a89a]" : "bg-transparent",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            "text-[15px] tracking-[-0.1px]",
                            unread ? "font-bold text-[#0f1720]" : "font-medium text-[#3f4c58]",
                          )}
                        >
                          {notification.title}
                        </p>
                        <p
                          className={cn(
                            "mt-0.5 whitespace-pre-line text-sm leading-snug",
                            unread ? "font-medium text-[#3f4c58]" : "font-normal text-[#64748b]",
                          )}
                        >
                          {notification.message}
                        </p>
                        <p className="mt-1.5 text-xs font-medium text-[#94a3b8]">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                          })}
                          {delivered ? " · Open issue" : ""}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          <div className="border-t border-[#e6edf1] p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full font-bold text-[#15776d]"
              onClick={() => navigate("/owner/notifications")}
            >
              View all notifications
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
